/**
 * Puzzle 1 — Gear Alignment.
 *
 * Basil uses Turn-Table Touch to spin two gears to target notches. Minna
 * uses AuraSight to reveal target indicators floating above each gear.
 * When both gears align, a brass bridge becomes fully opaque/visible.
 *
 * Both cats are required: a gear only seats (locks) on its target notch
 * while Minna's Aura Sight is revealing that gear's target indicator; with
 * the target unseen it clicks past and keeps turning. Before, every gear
 * auto-locked on its target, so Basil could spin both into place alone
 * and the indicators were decoration (tools/playtest/scenarios/solo-solve.mjs).
 * A gear already resting on its target seats the moment the target is
 * revealed.
 */
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";

import { Dial } from "../../../entities/interactives/Dial";
import { GameEvents } from "../../../config/events";
import { CLOCKWORK_CANOPY } from "../../../config/GameConfig";
import { tagAs } from "../../PlaceholderTag";

import type { PuzzleContext, PuzzleCue, PuzzleModule, PuzzleState } from "../../PuzzleModule";
import type { CueDef } from "../../../ui/tutorialCues";
import type { PuzzleQAHints } from "../../qa/PuzzleQAHints";

const CUE = {
  reveal: { cat: "minna", parts: [{ action: "aura" }, { text: "show the gear's mark" }] },
  grip: { cat: "basil", parts: [{ action: "turnStart" }, { text: "grip the gear" }] },
  spin: { cat: "basil", parts: [{ action: "rhythmHit" }, { text: "on the beat" }] },
} satisfies Record<string, CueDef>;
const OBJECTIVE = "Align the brass gears";

export class GearAlignmentPuzzle implements PuzzleModule {
  readonly id = "gearAlignment";
  readonly displayName = "Gear Alignment";

  private ctx!: PuzzleContext;

  private gearA!: Dial;
  private gearB!: Dial;
  private gearTargetA!: Mesh;
  private gearTargetB!: Mesh;
  private gearBridge!: Mesh;
  private bridgeMat!: PBRMaterial;

  private gearAAligned = false;
  private gearBAligned = false;
  private bridgeExtended = false;

  // Basil's Turn-Table Touch target tracking — which gear (if any) is the
  // current rhythm target. This is scene-local because only one gear at a
  // time can be the turntable target.
  private turnTableActive = false;
  private turnTableTarget: "canopyGearA" | "canopyGearB" | null = null;

  // Whether Aura Sight is currently revealing each gear's target indicator
  // (AuraSight drives onReveal/onConceal every frame Minna is in range).
  private targetRevealed = { canopyGearA: false, canopyGearB: false };
  private unseenTargetHintShown = false;

  setup(ctx: PuzzleContext): void {
    this.ctx = ctx;
    const { scene } = ctx;

    // ── Gears ──
    this.gearA = new Dial(scene, {
      key: "canopyGearA",
      position: new Vector3(12, 4.3, 0),
      totalNotches: CLOCKWORK_CANOPY.PUZZLE.GEAR_NOTCHES_A,
      targetNotch: CLOCKWORK_CANOPY.PUZZLE.GEAR_TARGET_A,
      label: "Canopy Gear A",
    });

    this.gearB = new Dial(scene, {
      key: "canopyGearB",
      position: new Vector3(20, 6.3, 0),
      totalNotches: CLOCKWORK_CANOPY.PUZZLE.GEAR_NOTCHES_B,
      targetNotch: CLOCKWORK_CANOPY.PUZZLE.GEAR_TARGET_B,
      label: "Canopy Gear B",
    });

    // ── Hidden target indicators (revealed by AuraSight) ──
    this.gearTargetA = createGearTargetIndicator(
      scene,
      new Vector3(12, 5.5, 0),
      "gearTargetA",
    );
    this.gearTargetB = createGearTargetIndicator(
      scene,
      new Vector3(20, 7.5, 0),
      "gearTargetB",
    );

    // ── Bridge (initially below the level; rises into place when solved) ──
    //
    // Originally created at the final y=3 with `visibility = 0.2` so it
    // looked faded-but-present. The QA pass found that left the physics
    // body fully active, which let players walk straight across the
    // unsolved bridge and skip the puzzle. Spawning the bridge below the
    // floor (y=-50) keeps the collider out of the play area; checkBridge
    // moves it into final position when both gears align.
    this.gearBridge = ctx.physicsSystem.createPlatform(
      "gearBridge",
      6,
      0.4,
      2,
      new Vector3(16, -50, 0),
    );
    this.bridgeMat = new PBRMaterial("bridgeMat", scene);
    this.bridgeMat.albedoColor = Color3.FromHexString(
      CLOCKWORK_CANOPY.COLORS.BRASS_WARM,
    );
    this.bridgeMat.metallic = 0.8;
    this.bridgeMat.roughness = 0.3;
    this.gearBridge.material = this.bridgeMat;
    this.gearBridge.visibility = 0.2;
    this.gearBridge.isVisible = false;

    // Tag gear targets and bridge as placeholder — these are procedural
    // CreateTorus / CreatePlatform calls used until the canopy art pass
    // delivers proper brass gear models.
    tagAs(this.gearTargetA, "placeholder");
    tagAs(this.gearTargetB, "placeholder");
    tagAs(this.gearBridge, "placeholder");

    // ── Register AuraSight interactives ──
    ctx.registerInteractive({
      mesh: this.gearA.rootMesh,
      hidden: false,
      state: "revealed",
    });
    ctx.registerInteractive({
      mesh: this.gearB.rootMesh,
      hidden: false,
      state: "revealed",
    });
    ctx.registerInteractive({
      mesh: this.gearTargetA,
      hidden: true,
      state: "hidden",
      onReveal: () => {
        this.gearTargetA.visibility = 0.6;
        this.targetRevealed.canopyGearA = true;
      },
      onConceal: () => {
        this.gearTargetA.visibility = 0;
        this.targetRevealed.canopyGearA = false;
      },
    });
    ctx.registerInteractive({
      mesh: this.gearTargetB,
      hidden: true,
      state: "hidden",
      onReveal: () => {
        this.gearTargetB.visibility = 0.6;
        this.targetRevealed.canopyGearB = true;
      },
      onConceal: () => {
        this.gearTargetB.visibility = 0;
        this.targetRevealed.canopyGearB = false;
      },
    });

    // ── Event subscriptions ──
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.DIAL_ALIGNED, (key) => {
        if (key !== "canopyGearA" && key !== "canopyGearB") return;
        if (this.targetRevealed[key]) {
          this.seatGear(key);
        } else if (!this.unseenTargetHintShown) {
          // Two-cat gate (see header): the gear turns past a target
          // nobody can see.
          this.unseenTargetHintShown = true;
          ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
            message: "The gear clicks past its mark: nobody can see where it should stop. Minna, hold Aura Sight (Q) near the gears.",
          });
        }
      }),
    );

    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.DIAL_TURNED, (key) => {
        if (key === "canopyGearA" && !this.gearA.aligned) this.gearAAligned = false;
        if (key === "canopyGearB" && !this.gearB.aligned) this.gearBAligned = false;
      }),
    );

    // Turn-Table Touch rhythm hit → spin the active gear
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.TURN_TABLE_TOUCH_HIT, () => {
        if (this.turnTableTarget === "canopyGearA") this.gearA.spin();
        else if (this.turnTableTarget === "canopyGearB") this.gearB.spin();
      }),
    );
  }

  update(_dt: number, _elapsed: number): void {
    // A gear left resting on its target seats as soon as Minna reveals it.
    if (!this.gearAAligned && this.gearA.aligned && this.targetRevealed.canopyGearA) {
      this.seatGear("canopyGearA");
    }
    if (!this.gearBAligned && this.gearB.aligned && this.targetRevealed.canopyGearB) {
      this.seatGear("canopyGearB");
    }

    const basilPos = this.ctx.basil.mesh.position;
    const distToGearA = Vector3.Distance(basilPos, this.gearA.rootMesh.position);
    const distToGearB = Vector3.Distance(basilPos, this.gearB.rootMesh.position);

    // Highlight nearest gear while still solvable
    if (distToGearA < 3 && !this.bridgeExtended) this.gearA.highlight();
    else this.gearA.unhighlight();
    if (distToGearB < 3 && !this.bridgeExtended) this.gearB.highlight();
    else this.gearB.unhighlight();

    // Auto-deactivate Turn-Table Touch if Basil walks away from target
    if (this.turnTableActive && this.turnTableTarget) {
      const target =
        this.turnTableTarget === "canopyGearA" ? this.gearA : this.gearB;
      const distToTarget = Vector3.Distance(basilPos, target.rootMesh.position);
      if (distToTarget >= 3) {
        this.ctx.basilAbilities.deactivateAbility("turnTableTouch");
        this.turnTableActive = false;
        this.turnTableTarget = null;
      }
    }
  }

  /**
   * Lock a gear on its (revealed) target notch. Auto-lock prevents further
   * spins from overshooting past the target — same pattern as L0
   * TutorialGate's dial fix.
   */
  private seatGear(gearKey: "canopyGearA" | "canopyGearB"): void {
    if (gearKey === "canopyGearA") {
      if (this.gearAAligned) return;
      this.gearAAligned = true;
      this.gearA.lock();
    } else {
      if (this.gearBAligned) return;
      this.gearBAligned = true;
      this.gearB.lock();
    }
    this.teardownTurnTableIfTargeting(gearKey);
    this.ctx.gameEvents.emit(GameEvents.PUZZLE_PROGRESSED, {
      puzzleId: this.id,
      step: gearKey === "canopyGearA" ? "gearAAligned" : "gearBAligned",
    });
    this.checkBridge();
  }

  /**
   * Tear down the rhythm ring the moment its gear locks into alignment.
   * Without this the ring kept spinning over a finished gear until Basil
   * happened to walk 3u away — reading as leftover debug UI
   * (2026-07-10 visual review, finding #7).
   */
  private teardownTurnTableIfTargeting(
    gearKey: "canopyGearA" | "canopyGearB",
  ): void {
    if (this.turnTableActive && this.turnTableTarget === gearKey) {
      this.ctx.basilAbilities.deactivateAbility("turnTableTouch");
      this.turnTableActive = false;
      this.turnTableTarget = null;
    }
  }

  onAbilityKey(key: string, pressed: boolean, _released: boolean): boolean {
    // 1 = toggle Turn-Table Touch on the closest gear
    if (key === "1" && pressed && !this.bridgeExtended) {
      const basilPos = this.ctx.basil.mesh.position;
      const distA = Vector3.Distance(basilPos, this.gearA.rootMesh.position);
      const distB = Vector3.Distance(basilPos, this.gearB.rootMesh.position);

      let targetGear: Dial | null = null;
      let targetKey: "canopyGearA" | "canopyGearB" | null = null;
      if (distA < 3) {
        targetGear = this.gearA;
        targetKey = "canopyGearA";
      } else if (distB < 3) {
        targetGear = this.gearB;
        targetKey = "canopyGearB";
      }

      if (targetGear && targetKey) {
        if (!this.turnTableActive || this.turnTableTarget !== targetKey) {
          // Switching targets: deactivate first
          if (this.turnTableActive) {
            this.ctx.basilAbilities.deactivateAbility("turnTableTouch");
            // Same-press target switch: deactivate() just armed the 0.2s
            // cooldown, which would make the holder refuse the immediate
            // reactivation below (this refusal existed before holder
            // routing too — activateAt() failed silently and
            // turnTableActive lied true). Zero the cooldown on this
            // explicit switch path only: holder-visible, event-clean,
            // and the deactivate/activate pair still emits a matched
            // DEACTIVATED → ACTIVATED sequence.
            this.ctx.turnTableTouch.cooldownRemaining = 0;
          }
          // Route through the holder so ABILITY_ACTIVATED fires for
          // HUD/telemetry/banter; latch state on the actual result so
          // turnTableActive can no longer lie true on a refusal.
          this.ctx.turnTableTouch.setTarget(
            targetGear.rootMesh.position,
            (_combo) => {},
          );
          this.turnTableActive =
            this.ctx.basilAbilities.activateAbility("turnTableTouch");
          this.turnTableTarget = this.turnTableActive ? targetKey : null;
        } else {
          this.ctx.basilAbilities.deactivateAbility("turnTableTouch");
          this.turnTableActive = false;
          this.turnTableTarget = null;
        }
        return true;
      }
      // No gear in range: DON'T consume the key. The scene routes ability
      // keys through every unsolved puzzle in array order and stops at the
      // first `true`; this puzzle is index 0, so returning true here
      // swallowed "1" for the whole level while the gears were unsolved —
      // notably shadowing TheOrrery's lens-ring tuning for players who
      // wander to the apex out of order.
      return false;
    }

    // 2 = rhythm hit
    if (key === "2" && pressed && this.turnTableActive) {
      this.ctx.turnTableTouch.attemptHit();
      return true;
    }

    return false;
  }

  getCue(): PuzzleCue | null {
    const key = !this.gearAAligned ? "canopyGearA" : !this.gearBAligned ? "canopyGearB" : null;
    if (!key) return null;
    const gear = key === "canopyGearA" ? this.gearA : this.gearB;
    const p = gear.rootMesh.position;
    const anchor = { x: p.x, y: p.y + 1.6, z: 0 };
    // A gear only seats on its mark while Minna's Aura Sight shows it.
    if (!this.targetRevealed[key]) return { objective: OBJECTIVE, cue: CUE.reveal, anchor };
    const spinning = this.turnTableActive && this.turnTableTarget === key;
    return { objective: OBJECTIVE, cue: spinning ? CUE.spin : CUE.grip, anchor };
  }

  getState(): PuzzleState {
    const steps = [this.gearAAligned, this.gearBAligned].filter(Boolean).length;
    return {
      solved: this.bridgeExtended,
      progress: this.bridgeExtended ? 1 : steps / 2,
    };
  }

  getQAHints(): PuzzleQAHints {
    // Canonical platform + gear geometry from setup() above, verbatim.
    // Keeping this co-located with the Dial positions means a gear
    // move forces an authoring-time update to the hint — no silent
    // drift between the puzzle and the QA harness.
    return {
      puzzleId: this.id,
      successEvent: GameEvents.PUZZLE_SOLVED,
      defaultStart: { minna: "plat_s1_1", basil: "plat_s1_1" },
      targets: [
        // Platforms (tops sit at these Y values per ClockworkCanopy.ts).
        { id: "plat_s1_1", position: { x: 7, y: 2.2 }, radius: 5.5, allowedCat: "either", label: "plat_s1_1 (spawn)" },
        { id: "plat_s1_2", position: { x: 16, y: 3.2 }, radius: 1.6, allowedCat: "either", label: "plat_s1_2" },
        { id: "plat_s1_3", position: { x: 12, y: 4.2 }, radius: 1.3, allowedCat: "either", label: "plat_s1_3 (gearA)" },
        // radius 1.7 (vs 1.3 platform half-width) gives the OvLeap
        // recipe enough arrival tolerance for the post-fire AIR_DECEL
        // wobble — basil reliably lands within plat_s1_4's footprint
        // (x=[18.5, 21.5]) but x=21.4 from the recipe's measured
        // settling overshot the strict 1.3u radius.
        { id: "plat_s1_4", position: { x: 20, y: 6.2 }, radius: 1.7, allowedCat: "either", label: "plat_s1_4 (gearB)" },
        // Gears — the rhythm targets. meshName uses the Dial key.
        { id: "gearA", meshName: "canopyGearA", position: { x: 12, y: 4.3 }, radius: 3, allowedCat: "basil", label: "Canopy Gear A" },
        { id: "gearB", meshName: "canopyGearB", position: { x: 20, y: 6.3 }, radius: 3, allowedCat: "basil", label: "Canopy Gear B" },
        // Staging marker east of plat_s1_4 (ground level). OvLeap west
        // from here lands on plat_s1_4 near gearB.
        //
        // Geometry contract (Q-031): plat_s1_4 was widened to w=5 →
        // footprint x=[17.5, 22.5] (ClockworkCanopy.ts platDefs), so the
        // ascent contact plane is east edge 22.5 + CAPSULE_RADIUS 0.35
        // = 22.85. The plan navigates here with toleranceX 0.5, so the
        // whole settle band must clear that plane — x=25 gives
        // [24.5, 25.5], ≥1.65u of clearance (the old x=22.9 left 0.05u
        // and Basil clipped the deck's east face at reduced fps).
        // East bound: stay ≥1.5u west of plat_s2_1's west edge (x=27).
        //
        // Co-op caveat: x=25 is reachable under the relaxed solo tether
        // (24u) with Minna parked at spawn x≈8.75; if this route ever
        // converts to navigatePair (strict 12u + 3u band), park Minna
        // at x≥13 first.
        { id: "ovleap_west_staging", position: { x: 25, y: 0.5 }, radius: 1.5, allowedCat: "basil", label: "OvLeap west staging (ground)" },
        // Under-gearA marker for Basil's straight-up jump onto plat_s1_3.
        { id: "under_gearA", position: { x: 11, y: 2.2 }, radius: 0.4, allowedCat: "basil", label: "Under plat_s1_3 (jump anchor)" },
      ],
      traversals: [
        // Straight-up jump from under_gearA (plat_s1_1 top, y=2.2) onto
        // plat_s1_3 (top y=4.2). Standing jump peak ≈ y=4.9 clears it.
        {
          id: "basil_jump_to_plat_s1_3",
          kind: "jump",
          cat: "basil",
          from: "under_gearA",
          to: "plat_s1_3",
          successWindowMs: 3000,
          recipe: [
            { key: " ", holdMs: 80 },
          ],
        },
        // OvLeap west from ground (x=25) onto plat_s1_4 (x=[17.5,22.5],
        // top y=6.2). OverclockedLeap has a 350 ms charge before the
        // impulse fires; fire from rest, climb purely vertically past
        // deck height, THEN air-drift west onto the deck. The 400 ms
        // post-impulse delay is the clip guard: the capsule bottom must
        // clear plat_s1_4's top (6.2) before any west vx exists, so the
        // deck's east face (contact plane x=22.85) can never be hit
        // during ascent — the old drift-at-impulse recipe clipped it
        // from x=22.9 at reduced fps (Q-031).
        //   t=0     keydown `4` → activate(), charging.
        //   t=350   charge complete, superJump: vy=+22.5, vx=0.
        //   t=750   rise=7.8u → capsule bottom ~7.84, 1.6u above the
        //           deck top — keydown `d`; AIR_ACCELERATION=18 ramps
        //           vx 0→-5 in 278 ms (-0.69u), then cruise 522 ms
        //           (-2.61u).
        //   t=1550  keyup `d`; AIR_DECELERATION=10 decays vx=-5→0
        //           over 500 ms (-1.25u); drift complete t=2050.
        //   Total drift -4.55u from launch x≈25 → land x≈20.45 on the
        //           deck [17.5,22.5]: ~2.95u west margin, ~2.05u east.
        //   t=1850  peak at center y≈17.6; the success poll
        //           (|x-20|≤1.7) is crossed at ~t=1550, right at
        //           recipe end — inside successWindowMs 5000.
        //   t≈2810  touchdown (FALL_MULTIPLIER 2.0, MAX_FALL_SPEED 15)
        //           — inside the plan's 3500 ms post-traverse sleep.
        // Scatter tuning: never shrink the 400 ms climb delay; if
        // low-fps landing scatter spills west, shorten the 800 ms
        // hold — if it spills east, lengthen it.
        {
          id: "basil_ovleap_to_plat_s1_4",
          kind: "ovLeap",
          cat: "basil",
          from: "ovleap_west_staging",
          to: "plat_s1_4",
          successWindowMs: 5000,
          recipe: [
            { keyDown: "4" },
            { waitMs: 50 },
            { keyUp: "4" },
            { waitMs: 300 }, // t=350: charge complete, impulse vy=+22.5 fires from rest
            // Pure vertical climb: at t=750 rise=7.8u → capsule bottom
            // ~7.84, 1.6u above plat_s1_4's top (6.2) — west drift can
            // no longer clip the deck's east face (contact plane x=22.85).
            { waitMs: 400 },
            { keyDown: "a" },
            { waitMs: 800 }, // ramp 278ms/-0.69u + cruise 522ms/-2.61u
            { keyUp: "a" }, // AIR_DECELERATION 10 decays vx over 500ms/-1.25u
          ],
        },
      ],
    };
  }

  /**
   * Sub-puzzle state seeder. Shape:
   *   {
   *     gearsAligned?: Array<"A" | "B">,  // which gear(s) to mark aligned
   *     solved?: boolean,                  // both gears aligned + bridge extended
   *   }
   *
   * `{ gearsAligned: ["A"] }` is useful for traces isolating the gearB
   * rhythm sequence (the harder gear — it sits higher and requires an
   * OvLeap). `{ solved: true }` extends the bridge and fires PUZZLE_SOLVED
   * for traces testing downstream puzzles that rely on crossing it.
   */
  seed(state: Record<string, unknown>): void {
    const raw = state.gearsAligned;
    const aligned = new Set<string>();
    if (Array.isArray(raw)) {
      for (const v of raw) {
        if (v === "A" || v === "B") aligned.add(v);
      }
    }
    if (state.solved === true) {
      aligned.add("A");
      aligned.add("B");
    }
    if (aligned.size === 0) return;

    if (aligned.has("A") && !this.gearAAligned) {
      this.gearAAligned = true;
      this.gearA.lock();
      this.ctx.gameEvents.emit(GameEvents.PUZZLE_PROGRESSED, {
        puzzleId: this.id,
        step: "gearAAligned",
      });
    }
    if (aligned.has("B") && !this.gearBAligned) {
      this.gearBAligned = true;
      this.gearB.lock();
      this.ctx.gameEvents.emit(GameEvents.PUZZLE_PROGRESSED, {
        puzzleId: this.id,
        step: "gearBAligned",
      });
    }
    this.checkBridge();
  }

  dispose(): void {
    this.gearA.dispose();
    this.gearB.dispose();
    // gearBridge is a physics platform — disposing the mesh releases the body
    this.gearBridge.dispose();
    this.bridgeMat.dispose();
    (this.gearTargetA.material as StandardMaterial | null)?.dispose();
    this.gearTargetA.dispose();
    (this.gearTargetB.material as StandardMaterial | null)?.dispose();
    this.gearTargetB.dispose();
  }

  // ─────────────────────────────────────────────────────────────────

  private checkBridge(): void {
    if (this.gearAAligned && this.gearBAligned && !this.bridgeExtended) {
      this.bridgeExtended = true;
      this.gearBridge.visibility = 1;
      this.gearBridge.isVisible = true;
      // Snap the bridge into its final position. The physics body follows
      // the mesh so the collider becomes standable in the same frame.
      this.gearBridge.position.y = 3.0;
      this.gearA.solve();
      this.gearB.solve();
      this.ctx.gameEvents.emit(GameEvents.PUZZLE_SOLVED, {
        puzzleId: this.id,
      });
      this.ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
        message: "Gear bridge extended!",
        color: CLOCKWORK_CANOPY.COLORS.BRASS_WARM,
      });
    }
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────

function createGearTargetIndicator(
  scene: import("@babylonjs/core/scene").Scene,
  position: Vector3,
  name: string,
): Mesh {
  const indicator = MeshBuilder.CreateTorus(
    name,
    { diameter: 1.5, thickness: 0.1, tessellation: 16 },
    scene,
  );
  indicator.position = position;
  const mat = new StandardMaterial(`${name}_mat`, scene);
  mat.diffuseColor = Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.LENS_GOLD);
  mat.emissiveColor = Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.LENS_GOLD).scale(0.5);
  mat.alpha = 0;
  indicator.material = mat;
  indicator.visibility = 0;
  indicator.isPickable = false;
  return indicator;
}
